import { datadogLogs } from '@datadog/browser-logs';
import { AttachmentScanStatus } from '../models/Attachment';
import { emailApiClient } from './emailApiClient';
import { getAttachmentStatus } from './uploadAttachment';

export interface EmailContentAttachment {
  fileId: string;
  fileName: string;
  scanStatus: AttachmentScanStatus;
}

export interface EmailContentTransfer {
  fromQueue: string;
  byAgent: string;
  comment: string;
}

export interface EmailContent {
  from?: string | null;
  to?: string[];
  cc?: string[];
  subject?: string | null;
  deliveredTo?: string | null;
  otherRecipients?: string[];
  bodyHtml?: string | null;
  attachments?: EmailContentAttachment[];
  transferInfo?: EmailContentTransfer | null;
}

export const fetchEmailContent = async (contactId: string): Promise<EmailContent> => {
  const params = new URLSearchParams({ contactId });
  const response = await emailApiClient(`/content?${params.toString()}`, {
    method: 'GET'
  });

  if (!response.ok) throw new Error(`API responded with status ${response.status}`);

  const json = (await response.json()) as { meta: { version: string }; data: EmailContent };
  return json.data;
};

const BODY_SCAN_POLL_INTERVAL_MS = 500;
const BODY_SCAN_POLL_TIMEOUT_MS = 60_000;

// Connect only serves a body file once its scan is approved, so a just-sent email's body may not be fetchable yet.
export const fetchEmailContentOnceBodyApproved = async (
  contactId: string,
  bodyFileId: string | undefined
): Promise<EmailContent | undefined> => {
  if (bodyFileId) {
    const timeoutAt = Date.now() + BODY_SCAN_POLL_TIMEOUT_MS;
    let { status } = await getAttachmentStatus(contactId, bodyFileId);

    while (status === 'scanning' && Date.now() < timeoutAt) {
      await new Promise(resolve => setTimeout(resolve, BODY_SCAN_POLL_INTERVAL_MS));
      ({ status } = await getAttachmentStatus(contactId, bodyFileId));
    }

    if (status !== 'approved') {
      datadogLogs.logger.warn('Sent email body not approved; showing composed body', {
        channel: 'email',
        contactId,
        status
      });
      return undefined;
    }
  }

  return fetchEmailContent(contactId);
};
